import { useEffect, useState } from 'react'
import logo from '../assets/logo.png'
import { Icono } from '../componentes/Icono.js'
import { definirClave, leerEnlace, type EnlaceDeClave } from '../datos/credenciales.js'
import { obtenerRepositorio } from '../datos/index.js'
import type { Jugador } from '../datos/index.js'
import { ir } from '../navegacion.js'

/**
 * Crear (o cambiar) la contraseña con el enlace que llegó por correo o por
 * WhatsApp.
 *
 * Es gemela del ingreso: la misma marca, la misma tarjeta, los mismos campos.
 *
 * Al terminar, el flujo se bifurca según quién es:
 *
 *   · El jugador entra directamente al site con la contraseña recién elegida:
 *     mandarlo al ingreso a escribir lo mismo otra vez sería pedirle dos veces
 *     lo que acaba de hacer.
 *   · El que organiza va al panel, con su usuario ya escrito. No se lo deja
 *     adentro del site: una cuenta que no juega ahí no tiene nada que ver. Y la
 *     sesión del panel no se puede abrir desde acá (es otra web), así que la
 *     contraseña la escribe una vez más, allá.
 *
 * La contraseña se escribe dos veces: es la primera vez que la elige, en un
 * teléfono, y no hay nadie a quien preguntarle si se equivocó al tipear.
 */
export function Clave({ token, alIngresar }: { token: string; alIngresar: (jugador: Jugador) => void }) {
  const [enlace, setEnlace] = useState<EnlaceDeClave | null>(null)
  const [problemaEnlace, setProblemaEnlace] = useState<string | null>(null)
  const [password, setPassword] = useState('')
  const [repetida, setRepetida] = useState('')
  const [verPassword, setVerPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [enviando, setEnviando] = useState(false)

  useEffect(() => {
    let vigente = true
    leerEnlace(token).then(
      (leido) => vigente && setEnlace(leido),
      (e: unknown) => vigente && setProblemaEnlace(e instanceof Error ? e.message : String(e)),
    )
    return () => {
      vigente = false
    }
  }, [token])

  async function enviar(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('La contraseña necesita al menos 8 caracteres.')
      return
    }
    if (password !== repetida) {
      setError('Las dos contraseñas no son iguales.')
      return
    }

    setEnviando(true)
    let usuario: string
    try {
      const resultado = await definirClave(token, password)
      usuario = resultado.usuario
      if (resultado.destino === 'panel') {
        window.location.assign(`${resultado.urlPanel.replace(/\/$/, '')}/?usuario=${encodeURIComponent(usuario)}`)
        return
      }
    } catch (problema) {
      setError(problema instanceof Error ? problema.message : String(problema))
      setEnviando(false)
      return
    }

    /* La contraseña ya quedó guardada. Si entrar falla (una cuenta que solo
       organiza, un corte de señal), lo que queda es ir al ingreso: no hay nada
       que repetir de este lado. */
    try {
      const repo = await obtenerRepositorio()
      alIngresar(await repo.ingresar(usuario, password))
      ir({ nombre: 'inicio' })
    } catch {
      ir({ nombre: 'ingreso' })
    }
  }

  const esNueva = enlace?.tipo !== 'reset_password'
  const organiza = enlace?.destino === 'panel'

  return (
    <div className="ingreso">
      <div className="ingreso-marca">
        <img src={logo} alt="" width={76} height={76} className="ingreso-logo" />
        <h1>DomiRoyal</h1>
        <p>
          {enlace
            ? esNueva
              ? `Hola ${enlace.nombre.split(' ')[0]}, crea tu contraseña para entrar.`
              : `Hola ${enlace.nombre.split(' ')[0]}, elige tu contraseña nueva.`
            : 'Tu contraseña de DomiRoyal.'}
        </p>
      </div>

      {problemaEnlace && (
        <div className="tarjeta ingreso-form">
          <p className="aviso error" role="alert">
            {problemaEnlace}
          </p>
          <button className="boton principal ingreso-entrar" type="button" onClick={() => ir({ nombre: 'ingreso' })}>
            Ir a entrar
          </button>
          <p className="ingreso-ayuda">
            Para un enlace nuevo, toca «¿Olvidaste tu contraseña?» al entrar, o pídeselo a quien
            organiza en tu club.
          </p>
        </div>
      )}

      {!enlace && !problemaEnlace && <p className="ingreso-ayuda">Revisando el enlace…</p>}

      {enlace && (
        <form className="tarjeta ingreso-form" onSubmit={enviar}>
          <div className="ingreso-campo">
            <span>Usuario</span>
            <div className="ingreso-entrada">
              <Icono nombre="persona" tamano={19} />
              <input value={enlace.usuario} readOnly autoComplete="username" />
            </div>
          </div>

          <label className="ingreso-campo">
            <span>{esNueva ? 'Contraseña' : 'Contraseña nueva'}</span>
            <div className="ingreso-entrada">
              <Icono nombre="candado" tamano={19} />
              <input
                type={verPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                enterKeyHint="next"
                placeholder="al menos 8 caracteres"
                minLength={8}
                required
                autoFocus
              />
              <button
                type="button"
                className="ingreso-ver"
                onClick={() => setVerPassword((v) => !v)}
                aria-pressed={verPassword}
              >
                {verPassword ? 'Ocultar' : 'Mostrar'}
              </button>
            </div>
          </label>

          <label className="ingreso-campo">
            <span>Repítela</span>
            <div className="ingreso-entrada">
              <Icono nombre="candado" tamano={19} />
              <input
                type={verPassword ? 'text' : 'password'}
                value={repetida}
                onChange={(e) => setRepetida(e.target.value)}
                autoComplete="new-password"
                enterKeyHint="go"
                placeholder="la misma otra vez"
                required
              />
            </div>
          </label>

          {error && (
            <p className="aviso error" role="alert">
              {error}
            </p>
          )}

          <button className="boton principal ingreso-entrar" type="submit" disabled={enviando}>
            {enviando
              ? 'Guardando…'
              : organiza
                ? esNueva
                  ? 'Crear contraseña e ir al panel'
                  : 'Cambiar e ir al panel'
                : esNueva
                  ? 'Crear contraseña y entrar'
                  : 'Cambiar y entrar'}
          </button>

          <p className="ingreso-ayuda">
            {organiza
              ? 'Es la contraseña para entrar al panel del organizador. Al terminar te llevamos allí.'
              : 'Es la contraseña para ver tus torneos, tu mesa y tu posición.'}
          </p>
        </form>
      )}
    </div>
  )
}
